import {Injectable} from '@angular/core';
import {
    ActivityBarChartDataDto,
    ChangeHistoryDto,
    InspectionChartComparisonRequest,
    InspectionResultCorrectionDto,
    InspectionResultDto,
    InspectionResultReviewDto,
    InspectionSummaryStatsDto,
    MaintenancePlanningService,
} from '@core/interfaces/engin/maintenance-planning/maintenance-planning';
import {MaintenancePlanningApi} from '@core/backend/engin/api/maintenace-planning/maintenance-planning.api';
import {Observable} from 'rxjs';
import {
    APIResponse,
    DataTableColumn,
    Filter,
    FilterFieldOptionRequest,
    FilterFieldOptionResponse,
    FilterFieldType,
    FilterOperatorType,
    GenericListRequest,
    PagingResponse,
    PresignedURL,
    Simple2DMultiSeries,
    SimpleMultiSeries,
    SimpleSeries,
    TableFilterConfig,
    YearQuarterPair,
} from '@core/interfaces/system/system-common';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {DownloadCSVUrl} from '@core/interfaces/engin/workflow-validation';
import {InspectionResultFullDto} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {AssetColumn} from '@core/interfaces/common/asset';
import {
    ActivityBulkUpdateDto,
    ActivityChangeStatus,
    ActivityCreateDto,
    ActivityDto,
    ActivityUpdateDto,
    CsvColumnRequest,
    DeficiencyBulkUpdateDto,
    DeficiencyChangeStatus,
    DeficiencyCreateDto,
    DeficiencyDto,
    DeficiencyFullDto,
    DeficiencyUpdateDto,
    InspectionStatsDto,
    LinkActivitiesToDeficiency,
} from '@core/interfaces/engin/maintenance-planning/deficiencies.model';

@Injectable()
export class MaintenancePlanningServiceImpl extends MaintenancePlanningService {
    constructor(private api: MaintenancePlanningApi) {
        super();
    }

    // Inspection Details
    findOneDto(resultId: number): Observable<InspectionResultDto> {
        return this.api.findOneDto(resultId);
    }

    findOneFullDto(resultId: number): Observable<InspectionResultFullDto> {
        return this.api.findOneFullDto(resultId);
    }

    getChangeHistory(resultId: number): Observable<ChangeHistoryDto[]> {
        return this.api.getChangeHistory(resultId);
    }

    submitReview(resultId: number, req: InspectionResultReviewDto): Observable<any> {
        return this.api.submitReview(resultId, req);
    }

    submitCorrection(resultId: number, req: InspectionResultCorrectionDto): Observable<InspectionResultFullDto> {
        return this.api.submitCorrection(resultId, req);
    }

    findLatestInspectionResult(resultId: number): Observable<APIResponse<InspectionResultDto>> {
        return this.api.findLatestInspectionResult(resultId);
    }

    getPresignedUrl(fileName: string, fileFormat: string): Observable<APIResponse<PresignedURL>> {
        return this.api.getPresignedUrl(fileName, fileFormat);
    }
    uploadToPresignedUrl(url: string, fileData: File): Observable<any> {
        return this.api.upload(`${url}`, fileData);
    }

    // Options
    getFilterOptions(): Observable<APIResponse<TableFilterConfig[]>> {
        return this.api.getFilterOptions();
    }

    getFilterFieldOptions(req: FilterFieldOptionRequest): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.getFilterFieldOptions(req);
    }

    getFilterFieldOptionsTable(
        fieldKey: string,
        searchString: string,
    ): Observable<APIResponse<FilterFieldOptionResponse>> {
        const req: FilterFieldOptionRequest = {
            fieldKey: fieldKey,
            searchString: searchString,
        };
        return this.getFilterFieldOptions(req);
    }

    // Inspection List
    listDto(): Observable<DataSource> {
        return this.api.listDto();
    }

    listDtoCsv(paramsString?: string): Observable<APIResponse<DownloadCSVUrl>> {
        return this.api.listDtoCsv(paramsString);
    }

    // Inspection Dashboard
    getGroupOptions(): Observable<APIResponse<AssetColumn[]>> {
        return this.api.getGroupOptions();
    }

    listInspectionAssetDto(req: GenericListRequest): Observable<DataSource> {
        return this.api.listInspectionAssetDto(req);
    }

    public getListInspectionAssetDtoCsv(paramsString: string, filterParams: Filter[]): Observable<DownloadCSVUrl> {
        return this.api.getListInspectionAssetDtoCsv(paramsString, filterParams);
    }

    getInspectionsByStatus(req: GenericListRequest): Observable<APIResponse<SimpleSeries<string, number>>> {
        return this.api.getInspectionsByStatus(req);
    }

    getInspectionsComparison(
        req: InspectionChartComparisonRequest,
    ): Observable<APIResponse<SimpleMultiSeries<string>>> {
        return this.api.getInspectionsComparison(req);
    }

    // Asset Registry > Maintenance
    getInspectionsTimelineByParentAssetId(
        parentAssetId: string,
    ): Observable<APIResponse<SimpleSeries<YearQuarterPair, number>>> {
        return this.api.getInspectionsTimelineByParentAssetId(parentAssetId);
    }

    getInspectionsSummaryByParentAssetId(parentAssetId: string): Observable<APIResponse<InspectionSummaryStatsDto>> {
        return this.api.getInspectionsSummaryByParentAssetId(parentAssetId);
    }

    // Inspection List
    listDtoByParentAssetId(assetId: string): Observable<DataSource> {
        return this.api.listDtoByParentAssetId(assetId);
    }

    listDtoCsvByParentAssetId(assetId: string, paramsString?: string): Observable<APIResponse<DownloadCSVUrl>> {
        return this.api.listDtoCsvByParentAssetId(assetId, paramsString);
    }

    // Deficiencies and Activities
    getDeficienciesDtoList(filterString?: string): Observable<DataSource> {
        return this.api.getDeficienciesDtoList(filterString);
    }

    getDeficienciesListByParentId(parentAssetId: string, filterString?: string): Observable<DataSource> {
        return this.api.getDeficienciesListByParentId(parentAssetId, filterString);
    }

    getDeficienciesByInspectionCode(inspectionResultCode: string): Observable<PagingResponse<DeficiencyDto>> {
        return this.api.getDeficienciesByInspectionId(inspectionResultCode);
    }

    downloadDeficienciesList(req: CsvColumnRequest, paramsString?: string): Observable<DownloadCSVUrl> {
        return this.api.downloadDeficienciesList(req, paramsString);
    }

    downloadDeficienciesListByParentId(
        parentAssetId: string,
        req: CsvColumnRequest,
        paramsString?: string,
    ): Observable<DownloadCSVUrl> {
        return this.api.downloadDeficienciesListByParentId(parentAssetId, req, paramsString);
    }

    updateDeficiencyStatus(statusChangeRequest: DeficiencyChangeStatus): Observable<any> {
        return this.api.updateDeficiencyStatus(statusChangeRequest);
    }

    createDeficiency(deficiency: DeficiencyCreateDto): Observable<any> {
        return this.api.createDeficiency(deficiency);
    }

    getDeficiencyFullDto(id: number): Observable<DeficiencyFullDto> {
        return this.api.getDeficiencyFullDto(id);
    }

    updateDeficiency(id: number, deficiency: DeficiencyUpdateDto): Observable<any> {
        return this.api.updateDeficiency(id, deficiency);
    }

    getDeficienciesFilterFieldOptionsTable(
        fieldKey: string,
        searchString: string,
        filterParams: Filter[] = [],
    ): Observable<APIResponse<FilterFieldOptionResponse>> {
        const req: FilterFieldOptionRequest = {
            fieldKey,
            searchString,
            filterParams,
        };
        return this.api.getDeficienciesFilterFieldOptions(req);
    }

    getDeficienciesFilterFieldOptionsTableByParentId(
        parentAssetId,
        fieldKey: string,
        searchString: string,
        filterParams: Filter[] = [],
    ): Observable<APIResponse<FilterFieldOptionResponse>> {
        const req: FilterFieldOptionRequest = {
            fieldKey,
            searchString,
            filterParams,
        };
        return this.api.getDeficienciesFilterFieldOptionsByParentId(parentAssetId, req);
    }

    getDeficienciesCategories(searchString: string): Observable<string[]> {
        return this.api.getDeficienciesCategories(searchString);
    }

    getDeficienciesColumns(): Observable<DataTableColumn[]> {
        return this.api.getDeficienciesColumns();
    }

    bulkUpdateDeficiency(req: DeficiencyBulkUpdateDto): Observable<any> {
        return this.api.bulkUpdateDeficiency(req);
    }

    getDeficienciesTimelineByAsset(assetId: string): Observable<APIResponse<SimpleSeries<YearQuarterPair, number>>> {
        return this.api.getDeficienciesTimelineByAsset(assetId);
    }

    getDeficienciesBarChartByAsset(assetId: string): Observable<APIResponse<SimpleSeries<string, number>>> {
        return this.api.getDeficienciesBarChartByAsset(assetId);
    }

    getActivityTimelineByAsset(assetId: string): Observable<APIResponse<Simple2DMultiSeries<YearQuarterPair, string>>> {
        return this.api.getActivityTimelineByAsset(assetId);
    }

    getActivityBarChartByAsset(
        assetId: string,
    ): Observable<APIResponse<SimpleSeries<string, ActivityBarChartDataDto>>> {
        return this.api.getActivityBarChartByAsset(assetId);
    }

    getActivitiesDtoList(filterString?: string): Observable<DataSource> {
        return this.api.getActivitiesDtoList(filterString);
    }

    getActivitiesListByParentId(parentAssetId: string, filterString?: string) {
        return this.api.getActivitiesListByParentId(parentAssetId, filterString);
    }

    getActivitiesList(filterString?: string): Observable<PagingResponse<ActivityDto>> {
        return this.api.getActivitiesList(filterString);
    }

    downloadActivitiesList(req: CsvColumnRequest, paramsString?: string): Observable<DownloadCSVUrl> {
        return this.api.downloadActivitiesList(req, paramsString);
    }

    downloadActivitiesListByParentId(
        parentAssetId: string,
        req: CsvColumnRequest,
        paramsString?: string,
    ): Observable<DownloadCSVUrl> {
        return this.api.downloadActivitiesListByParentId(parentAssetId, req, paramsString);
    }

    createActivity(activity: ActivityCreateDto): Observable<APIResponse<ActivityDto>> {
        return this.api.createActivity(activity);
    }

    updateActivityStatus(statusChangeRequest: ActivityChangeStatus): Observable<any> {
        return this.api.updateActivityStatus(statusChangeRequest);
    }

    getActivityFullDto(id: number): Observable<ActivityDto> {
        return this.api.getActivityFullDto(id);
    }

    updateActivity(id: number, activity: ActivityUpdateDto): Observable<any> {
        return this.api.updateActivity(id, activity);
    }

    getActivityCategories(searchString: string): Observable<string[]> {
        return this.api.getActivityCategories(searchString);
    }

    getInspectionStats(id: number): Observable<InspectionStatsDto> {
        return this.api.getInspectionStats(id);
    }
    getActivitiesColumns(): Observable<DataTableColumn[]> {
        return this.api.getActivitiesColumns();
    }
    bulkLinkDeficiency(req: LinkActivitiesToDeficiency): Observable<any> {
        return this.api.bulkLinkDeficiency(req);
    }

    getActivitiesFilterFieldOptionsTable(
        fieldKey: string,
        searchString: string,
        filterParams: Filter[] = [],
    ): Observable<APIResponse<FilterFieldOptionResponse>> {
        const req: FilterFieldOptionRequest = {
            fieldKey,
            searchString,
            filterParams,
        };
        return this.api.getActivitiesFilterFieldOptions(req);
    }

    getActivitiesFilterFieldOptionsTableByParentId(
        parentAssetId,
        fieldKey: string,
        searchString: string,
        filterParams: Filter[] = [],
    ): Observable<APIResponse<FilterFieldOptionResponse>> {
        const req: FilterFieldOptionRequest = {
            fieldKey,
            searchString,
            filterParams,
        };
        return this.api.getActivitiesFilterFieldOptionsByParentId(parentAssetId, req);
    }

    bulkUpdateActivity(req: ActivityBulkUpdateDto): Observable<any> {
        return this.api.bulkUpdateActivity(req);
    }

    scanForDeficiencies(inspectionIds: number[]): Observable<number> {
        return this.api.scanForDeficiencies(inspectionIds);
    }
}
